<template>
  <div class="settings-page">
    <!-- Background Elements -->
    <div class="page-grid"></div>
    <div class="page-orb orb-1"></div>
    <div class="page-orb orb-2"></div>

    <div class="settings-container">
      <!-- Header -->
      <div class="settings-header">
        <div class="header-content">
          <h1 class="header-title">
            Manage My Settings
            <span v-if="isCEO" class="ceo-page-badge">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M5 19H19V21H5V19ZM5 8L8.5 11L12 5L15.5 11L19 8L18 17H6L5 8Z"/>
              </svg>
              CEO
            </span>
          </h1>
          <p class="header-subtitle">Personalize your experience and keep your profile up to date.</p>
        </div>
        <button class="btn-back" @click="$router.push('/staff-dashboard')">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Back to Dashboard
        </button>
      </div>

      <div class="settings-grid">
        <!-- LEFT SIDEBAR -->
        <div class="sidebar-card">
          <!-- Avatar Section -->
          <div class="avatar-section">
            <div class="avatar-wrapper">
              <div v-if="photoURL" class="avatar" :style="{ backgroundImage: `url(${photoURL})` }"></div>
              <div v-else class="avatar avatar-placeholder" :style="{ background: avatarGradient }">
                {{ initials }}
              </div>
              <button class="avatar-edit" @click="$refs.avatarInput.click()">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M11.3333 2.00001C11.5084 1.82491 11.716 1.68602 11.9441 1.59126C12.1722 1.49649 12.4165 1.44769 12.6633 1.44769C12.9101 1.44769 13.1544 1.49649 13.3825 1.59126C13.6106 1.68602 13.8183 1.82491 13.9933 2.00001C14.1684 2.17511 14.3073 2.38276 14.4021 2.61088C14.4968 2.83899 14.5456 3.08329 14.5456 3.33001C14.5456 3.57673 14.4968 3.82103 14.4021 4.04914C14.3073 4.27726 14.1684 4.48491 13.9933 4.66001L5.00001 13.6533L1.33334 14.6667L2.34668 11L11.3333 2.00001Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
              <input
                ref="avatarInput"
                type="file"
                accept="image/*"
                @change="updateAvatar"
                style="display: none"
              />
            </div>
            <div class="user-info">
              <h2 class="user-name">
                {{ displayName || 'User' }}
                <span v-if="isCEO" class="ceo-inline-tag">CEO</span>
              </h2>
              <p class="user-email">{{ email }}</p>
            </div>
          </div>

          <!-- Points Card -->
          <div class="points-card">
            <div class="points-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="points-content">
              <div class="points-label">Your Points</div>
              <div class="points-value">{{ points }}</div>
              <div class="points-today">+{{ pointsToday }} today</div>
              <div class="points-progress">
                <div class="progress-bar">
                  <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
                </div>
                <div class="progress-label">Next badge in {{ pointsToNextBadge }} pts</div>
              </div>
            </div>
          </div>

          <!-- Activity Card -->
          <div class="activity-card">
            <div class="activity-header">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M2.25 15.75L6.75 9.75L10.125 12.375L15.75 5.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M11.25 5.25H15.75V9.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>Your Activity</span>
            </div>

            <div v-if="activityLoaded && totalActivity === 0" class="activity-empty">
              Log a call, email, or meeting to see your activity here.
            </div>

            <template v-else>
              <div class="activity-chart">
                <svg viewBox="0 0 280 80" width="100%" height="80" preserveAspectRatio="none">
                  <polyline
                    :points="activityPoints"
                    fill="none"
                    stroke="var(--gold-light)"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>

              <div class="activity-stats">
                <div class="stat-item">
                  <span class="stat-value">{{ stats.calls }}</span>
                  <span class="stat-label">Calls</span>
                </div>
                <span class="stat-divider">·</span>
                <div class="stat-item">
                  <span class="stat-value">{{ stats.emails }}</span>
                  <span class="stat-label">Emails</span>
                </div>
                <span class="stat-divider">·</span>
                <div class="stat-item">
                  <span class="stat-value">{{ stats.meetings }}</span>
                  <span class="stat-label">Meetings</span>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- RIGHT CONTENT -->
        <div class="content-card">
          <!-- Tabs -->
          <div class="tabs-header">
            <button
              v-for="(tabItem, idx) in tabs"
              :key="idx"
              :class="['tab-button', { active: activeTab === idx }]"
              @click="activeTab = idx"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" v-html="tabItem.icon"></svg>
              <span>{{ tabItem.label }}</span>
            </button>
          </div>

          <!-- Tab Content -->
          <div class="tab-content">
            <!-- PROFILE TAB -->
            <div v-if="activeTab === 0" class="tab-panel">
              <form @submit.prevent="saveProfile" class="settings-form">
                <div class="form-group">
                  <label>Full Name</label>
                  <input
                    v-model="profileForm.displayName"
                    type="text"
                    placeholder="Enter your full name"
                  />
                </div>

                <div class="form-group">
                  <label>Email</label>
                  <input
                    v-model="profileForm.email"
                    type="email"
                    disabled
                  />
                  <p class="form-hint">Email cannot be changed</p>
                </div>

                <div class="form-group">
                  <label>Phone Number</label>
                  <input
                    v-model="profileForm.phone"
                    type="tel"
                    placeholder="Enter your phone number"
                  />
                </div>

                <div class="form-group">
                  <label>Address</label>
                  <textarea
                    v-model="profileForm.address"
                    rows="3"
                    placeholder="Enter your address"
                  ></textarea>
                </div>

                <button type="submit" class="btn-save" :disabled="savingProfile">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M15 2.5H5C4.17157 2.5 3.5 3.17157 3.5 4V16C3.5 16.8284 4.17157 17.5 5 17.5H15C15.8284 17.5 16.5 16.8284 16.5 16V4C16.5 3.17157 15.8284 2.5 15 2.5Z" stroke="currentColor" stroke-width="1.5"/>
                    <path d="M12.5 2.5V6.5H7.5V2.5" stroke="currentColor" stroke-width="1.5"/>
                    <path d="M7.5 11H12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                    <path d="M7.5 14H12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                  {{ savingProfile ? 'Saving...' : 'Save Changes' }}
                </button>
              </form>
            </div>

            <!-- SECURITY TAB -->
            <div v-if="activeTab === 1" class="tab-panel">
              <form @submit.prevent="changePassword" class="settings-form">
                <div class="security-notice">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <div>
                    <div class="notice-title">Secure Your Account</div>
                    <div class="notice-text">Keep your password strong and unique</div>
                  </div>
                </div>

                <div class="form-group">
                  <label>Current Password</label>
                  <div class="password-input">
                    <input
                      v-model="passwordForm.currentPassword"
                      :type="showCurrentPassword ? 'text' : 'password'"
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      class="btn-toggle-password"
                      @click="showCurrentPassword = !showCurrentPassword"
                    >
                      <svg v-if="showCurrentPassword" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M2.5 10C2.5 10 5 4.16666 10 4.16666C15 4.16666 17.5 10 17.5 10C17.5 10 15 15.8333 10 15.8333C5 15.8333 2.5 10 2.5 10Z" stroke="currentColor" stroke-width="1.5"/>
                        <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="currentColor" stroke-width="1.5"/>
                      </svg>
                      <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M14.95 14.95C13.5255 16.0358 11.7909 16.6374 10 16.6667C5 16.6667 2.5 10.8333 2.5 10.8333C3.35906 9.06829 4.52523 7.48565 5.94166 6.16666M8.25 4.53332C8.82365 4.39904 9.41093 4.33189 10 4.33332C15 4.33332 17.5 10.1667 17.5 10.1667C17.0125 11.0746 16.4208 11.9282 15.7333 12.7167M11.7667 11.7667C11.5378 12.0123 11.2617 12.2093 10.9551 12.3459C10.6485 12.4826 10.3176 12.556 9.98208 12.5619C9.64652 12.5678 9.31332 12.506 9.00185 12.3804C8.69037 12.2547 8.40721 12.0676 8.16937 11.8298C7.93153 11.5919 7.7444 11.3088 7.61876 10.9973C7.49312 10.6858 7.43128 10.3526 7.43718 10.0171C7.44307 9.68152 7.51658 9.3506 7.65325 9.04397C7.78992 8.73735 7.98691 8.46125 8.23249 8.23232" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M2.5 2.5L17.5 17.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                      </svg>
                    </button>
                  </div>
                </div>

                <div class="form-group">
                  <label>New Password</label>
                  <div class="password-input">
                    <input
                      v-model="passwordForm.newPassword"
                      :type="showNewPassword ? 'text' : 'password'"
                      placeholder="Enter new password"
                      @input="checkPasswordStrength"
                    />
                    <button
                      type="button"
                      class="btn-toggle-password"
                      @click="showNewPassword = !showNewPassword"
                    >
                      <svg v-if="showNewPassword" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M2.5 10C2.5 10 5 4.16666 10 4.16666C15 4.16666 17.5 10 17.5 10C17.5 10 15 15.8333 10 15.8333C5 15.8333 2.5 10 2.5 10Z" stroke="currentColor" stroke-width="1.5"/>
                        <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="currentColor" stroke-width="1.5"/>
                      </svg>
                      <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M14.95 14.95C13.5255 16.0358 11.7909 16.6374 10 16.6667C5 16.6667 2.5 10.8333 2.5 10.8333C3.35906 9.06829 4.52523 7.48565 5.94166 6.16666M8.25 4.53332C8.82365 4.39904 9.41093 4.33189 10 4.33332C15 4.33332 17.5 10.1667 17.5 10.1667C17.0125 11.0746 16.4208 11.9282 15.7333 12.7167M11.7667 11.7667C11.5378 12.0123 11.2617 12.2093 10.9551 12.3459C10.6485 12.4826 10.3176 12.556 9.98208 12.5619C9.64652 12.5678 9.31332 12.506 9.00185 12.3804C8.69037 12.2547 8.40721 12.0676 8.16937 11.8298C7.93153 11.5919 7.7444 11.3088 7.61876 10.9973C7.49312 10.6858 7.43128 10.3526 7.43718 10.0171C7.44307 9.68152 7.51658 9.3506 7.65325 9.04397C7.78992 8.73735 7.98691 8.46125 8.23249 8.23232" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M2.5 2.5L17.5 17.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                      </svg>
                    </button>
                  </div>
                  <div v-if="passwordForm.newPassword" class="password-strength">
                    <div class="strength-bar">
                      <div
                        class="strength-fill"
                        :style="{ width: passwordStrength.percent + '%', background: passwordStrength.color }"
                      ></div>
                    </div>
                    <span class="strength-label" :style="{ color: passwordStrength.color }">
                      {{ passwordStrength.label }}
                    </span>
                  </div>
                </div>

                <div class="form-group">
                  <label>Confirm New Password</label>
                  <input
                    v-model="passwordForm.confirmPassword"
                    :type="showNewPassword ? 'text' : 'password'"
                    placeholder="Confirm new password"
                  />
                </div>

                <div v-if="passwordError" class="form-error">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z" stroke="currentColor" stroke-width="1.5"/>
                    <path d="M8 5V8.5M8 11H8.005" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                  {{ passwordError }}
                </div>

                <button type="submit" class="btn-save" :disabled="changingPassword">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M15 8.75V6.25C15 5.91848 14.8683 5.60054 14.6339 5.36612C14.3995 5.1317 14.0815 5 13.75 5H6.25C5.91848 5 5.60054 5.1317 5.36612 5.36612C5.1317 5.60054 5 5.91848 5 6.25V13.75C5 14.0815 5.1317 14.3995 5.36612 14.6339C5.60054 14.8683 5.91848 15 6.25 15H8.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M10 10H16.25M16.25 10L14.375 8.125M16.25 10L14.375 11.875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  {{ changingPassword ? 'Changing...' : 'Change Password' }}
                </button>
              </form>

              <!-- Two-Factor Authentication -->
              <div class="security-section">
                <div class="section-header">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 18.3333C10 18.3333 16.6667 15 16.6667 10V4.16667L10 1.66667L3.33334 4.16667V10C3.33334 15 10 18.3333 10 18.3333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span>Two-Factor Authentication</span>
                </div>
                <div class="twofa-row">
                  <div class="twofa-info">
                    <div class="twofa-title">Require a second step at sign-in</div>
                    <div class="twofa-subtitle">{{ twoFactorEnabled ? 'Enabled — your account has extra protection' : 'Not enabled yet' }}</div>
                  </div>
                  <label class="toggle-switch">
                    <input type="checkbox" :checked="twoFactorEnabled" @change="toggleTwoFactor" />
                    <span class="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <div v-if="snack.show" class="toast-notification">
      <span>{{ snack.msg }}</span>
      <button @click="snack.show = false">✕</button>
    </div>
  </div>
</template>

<script>
import { auth, db, storage } from '@/firebase'
import { updateProfile, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth'
import {
  doc, getDoc, setDoc, updateDoc, onSnapshot,
  collectionGroup, query, where, getDocs
} from 'firebase/firestore'
import { ref as sRef, uploadBytes, getDownloadURL } from 'firebase/storage'

export default {
  name: 'StaffSettings',
  data() {
    return {
      activeTab: 0,
      tabs: [
        {
          label: 'Profile',
          icon: '<path d="M10 11.6667C12.7614 11.6667 15 9.42811 15 6.66667C15 3.90524 12.7614 1.66667 10 1.66667C7.23858 1.66667 5 3.90524 5 6.66667C5 9.42811 7.23858 11.6667 10 11.6667Z" stroke="currentColor" stroke-width="1.5"/><path d="M3.33334 18.3333C3.33334 16.5652 4.03572 14.8695 5.28596 13.6193C6.5362 12.369 8.23189 11.6667 10 11.6667C11.7681 11.6667 13.4638 12.369 14.7141 13.6193C15.9643 14.8695 16.6667 16.5652 16.6667 18.3333" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>'
        },
        {
          label: 'Security',
          icon: '<path d="M10 18.3333C10 18.3333 16.6667 15 16.6667 10V4.16667L10 1.66667L3.33334 4.16667V10C3.33334 15 10 18.3333 10 18.3333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>'
        }
      ],

      // User data
      email: '',
      displayName: '',
      photoURL: '',
      // `role` (checked by route guards) stays 'Staff' for someone like
      // Mike — `type` is a separate Firestore field ('CEO') we read
      // purely to change how this page presents itself to him.
      userType: 'Staff',

      // Live points/streak data from the `user_stats` collection (same
      // source of truth used by the leads view)
      userStats: {
        points: 0,
        todayPoints: 0,
        streakDays: 0,
        lastActionDate: null
      },
      unsubStats: null,
      badgeTiers: [
        { threshold: 100, label: 'Rookie' },
        { threshold: 500, label: 'Closer' },
        { threshold: 1500, label: 'Rainmaker' },
        { threshold: 3000, label: 'Elite' }
      ],

      // Profile form
      profileForm: {
        displayName: '',
        email: '',
        phone: '',
        address: ''
      },
      savingProfile: false,

      // Password form
      passwordForm: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      showCurrentPassword: false,
      showNewPassword: false,
      changingPassword: false,
      passwordError: '',
      passwordStrength: {
        percent: 0,
        label: '',
        color: ''
      },

      // Two-factor
      twoFactorEnabled: false,

      // Activity data — populated from the `interactions` subcollections
      // logged against leads (see loadActivityStats)
      activityData: [],
      activityLoaded: false,
      stats: {
        calls: 0,
        emails: 0,
        meetings: 0
      },

      // UI
      snack: { show: false, msg: '' }
    }
  },

  computed: {
    isCEO() {
      return (this.userType || '').toLowerCase() === 'ceo'
    },
    initials() {
      return this.displayName
        .split(' ')
        .map(n => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    },
    avatarGradient() {
      return 'linear-gradient(135deg, #F0D080, #C9A84C)'
    },
    points() {
      return this.userStats.points || 0
    },
    pointsToday() {
      const todayKey = new Date().toDateString()
      return this.userStats.lastActionDate === todayKey ? (this.userStats.todayPoints || 0) : 0
    },
    nextBadgeTier() {
      return this.badgeTiers.find(tier => this.points < tier.threshold) || null
    },
    progressPercent() {
      if (!this.nextBadgeTier) return 100
      const prevThreshold = [...this.badgeTiers]
        .reverse()
        .find(tier => tier.threshold <= this.points)?.threshold || 0
      const span = this.nextBadgeTier.threshold - prevThreshold
      const progressed = this.points - prevThreshold
      return Math.min(100, Math.round((progressed / span) * 100))
    },
    pointsToNextBadge() {
      if (!this.nextBadgeTier) return 0
      return this.nextBadgeTier.threshold - this.points
    },
    totalActivity() {
      return this.stats.calls + this.stats.emails + this.stats.meetings
    },
    activityPoints() {
      const width = 280
      const height = 80
      const padding = 10
      const maxVal = Math.max(1, ...this.activityData)
      const points = this.activityData.map((val, i) => {
        const x = (i / (this.activityData.length - 1)) * (width - padding * 2) + padding
        const y = height - ((val / maxVal) * (height - padding * 2)) - padding
        return `${x},${y}`
      })
      return points.join(' ')
    }
  },

  async created() {
    const user = auth.currentUser
    if (!user) {
      this.$router.push('/')
      return
    }

    this.email = user.email
    this.displayName = user.displayName || 'User'
    this.photoURL = user.photoURL || ''

    this.profileForm.displayName = this.displayName
    this.profileForm.email = this.email

    // Load additional user data from Firestore
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid))
      if (userDoc.exists()) {
        const data = userDoc.data()
        this.profileForm.phone = data.phone || ''
        this.profileForm.address = data.address || ''
        this.twoFactorEnabled = data.twoFactorEnabled || false
        this.userType = data.type || 'Staff'
      }
    } catch (e) {
      console.error('Error loading user data:', e)
    }

    // Live points/streak, same source of truth as the leads view
    const statsRef = doc(db, 'user_stats', user.uid)
    this.unsubStats = onSnapshot(statsRef, snap => {
      if (snap.exists()) {
        this.userStats = Object.assign({
          points: 0,
          todayPoints: 0,
          streakDays: 0,
          lastActionDate: null
        }, snap.data())
      }
    })

    this.loadActivityStats()
  },

  beforeUnmount() {
    if (this.unsubStats) this.unsubStats()
  },

  methods: {
    async loadActivityStats() {
      const user = auth.currentUser
      if (!user) return

      try {
        const interactionsQuery = query(
          collectionGroup(db, 'interactions'),
          where('by', '==', user.uid)
        )
        const snap = await getDocs(interactionsQuery)

        const counts = { call: 0, email: 0, meeting: 0 }

        // Last 14 days, oldest first, for the sparkline
        const now = new Date()
        const dayKeys = []
        const dayBuckets = {}
        for (let i = 13; i >= 0; i--) {
          const key = new Date(now.getTime() - i * 24 * 60 * 60 * 1000).toDateString()
          dayKeys.push(key)
          dayBuckets[key] = 0
        }

        snap.forEach(docSnap => {
          const data = docSnap.data()
          if (counts[data.type] !== undefined) counts[data.type]++

          const ts = data.at
          if (ts) {
            const date = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000)
            const key = date.toDateString()
            if (dayBuckets[key] !== undefined) dayBuckets[key]++
          }
        })

        this.stats = counts
        this.activityData = dayKeys.map(key => dayBuckets[key])
      } catch (e) {
        console.error('Error loading activity stats:', e)
      } finally {
        this.activityLoaded = true
      }
    },

    async updateAvatar(e) {
      const file = e.target.files[0]
      if (!file) return

      try {
        const user = auth.currentUser
        const path = `avatars/${user.uid}/${Date.now()}_${file.name}`
        const storageRef = sRef(storage, path)
        await uploadBytes(storageRef, file)
        const url = await getDownloadURL(storageRef)

        await updateProfile(user, { photoURL: url })
        this.photoURL = url

        this.snack = { show: true, msg: '✓ Avatar updated' }
      } catch (e) {
        console.error('Avatar update error:', e)
        this.snack = { show: true, msg: '✗ Failed to update avatar' }
      }
    },

    async saveProfile() {
      this.savingProfile = true
      try {
        const user = auth.currentUser

        // Update auth profile
        await updateProfile(user, {
          displayName: this.profileForm.displayName
        })

        // Update Firestore
        await setDoc(doc(db, 'users', user.uid), {
          displayName: this.profileForm.displayName,
          email: this.profileForm.email,
          phone: this.profileForm.phone,
          address: this.profileForm.address,
          updatedAt: new Date()
        }, { merge: true })

        this.displayName = this.profileForm.displayName
        this.snack = { show: true, msg: '✓ Profile updated successfully' }
      } catch (e) {
        console.error('Profile update error:', e)
        this.snack = { show: true, msg: '✗ Failed to update profile' }
      } finally {
        this.savingProfile = false
      }
    },

    checkPasswordStrength() {
      const password = this.passwordForm.newPassword
      if (!password) {
        this.passwordStrength = { percent: 0, label: '', color: '' }
        return
      }

      let strength = 0
      if (password.length >= 8) strength += 25
      if (password.length >= 12) strength += 25
      if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25
      if (/[0-9]/.test(password)) strength += 12.5
      if (/[^a-zA-Z0-9]/.test(password)) strength += 12.5

      let label = 'Weak'
      let color = '#EF9A9A'
      if (strength >= 75) {
        label = 'Strong'
        color = '#F0D080'
      } else if (strength >= 50) {
        label = 'Medium'
        color = '#C9A84C'
      }

      this.passwordStrength = { percent: strength, label, color }
    },

    async changePassword() {
      this.passwordError = ''

      if (!this.passwordForm.currentPassword || !this.passwordForm.newPassword) {
        this.passwordError = 'Please fill in all fields'
        return
      }

      if (this.passwordForm.newPassword.length < 8) {
        this.passwordError = 'Password must be at least 8 characters'
        return
      }

      if (this.passwordForm.newPassword !== this.passwordForm.confirmPassword) {
        this.passwordError = 'Passwords do not match'
        return
      }

      this.changingPassword = true
      try {
        const user = auth.currentUser
        const credential = EmailAuthProvider.credential(
          user.email,
          this.passwordForm.currentPassword
        )

        await reauthenticateWithCredential(user, credential)
        await updatePassword(user, this.passwordForm.newPassword)

        this.passwordForm = {
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }
        this.snack = { show: true, msg: '✓ Password changed successfully' }
      } catch (e) {
        console.error('Password change error:', e)
        if (e.code === 'auth/wrong-password') {
          this.passwordError = 'Current password is incorrect'
        } else {
          this.passwordError = 'Failed to change password'
        }
      } finally {
        this.changingPassword = false
      }
    },

    async toggleTwoFactor() {
      try {
        const user = auth.currentUser
        const newStatus = !this.twoFactorEnabled

        await updateDoc(doc(db, 'users', user.uid), {
          twoFactorEnabled: newStatus
        })

        this.twoFactorEnabled = newStatus
        this.snack = {
          show: true,
          msg: newStatus ? '✓ 2FA enabled' : '✓ 2FA disabled'
        }
      } catch (e) {
        console.error('2FA toggle error:', e)
        this.snack = { show: true, msg: '✗ Failed to update 2FA' }
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@500;600;700;800&family=Cormorant+Garamond:wght@400;500;600&display=swap');

/* ==================== PAGE BACKGROUND ==================== */
.settings-page {
  --bg-page: #08090a;
  --bg-alt: #0f1012;
  --bg-card: #0f1012;
  --bg-card-hover: #17181c;
  --border-subtle: rgba(255, 255, 255, 0.09);
  --border-gold: rgba(201, 168, 76, 0.35);
  --text-primary: #F5F0E8;
  --text-secondary: rgba(245, 240, 232, 0.68);
  --text-muted: rgba(245, 240, 232, 0.48);
  --gold: #C9A84C;
  --gold-light: #F0D080;
  --gold-dark: #9C7A34;
  --graphite: #6A6D74;

  min-height: 100vh;
  background: linear-gradient(160deg, #101114 0%, #08090a 55%, #050506 100%);
  position: relative;
  overflow-x: hidden;
  font-family: 'Syne', -apple-system, BlinkMacSystemFont, sans-serif;
  color: var(--text-primary);
  padding: 40px 0 80px;
}

.settings-page .header-title,
.settings-page .user-name,
.settings-page .points-value {
  font-family: 'Cormorant Garamond', Georgia, serif;
  letter-spacing: -0.005em;
}

.page-grid {
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(to right, rgba(255, 255, 255, 0.06) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255, 255, 255, 0.06) 1px, transparent 1px);
  background-size: 60px 60px;
  mask-image: radial-gradient(ellipse 70% 60% at 50% 50%, black 20%, transparent 70%);
  pointer-events: none;
  z-index: 0;
}

.page-orb {
  position: fixed;
  border-radius: 50%;
  filter: blur(150px);
  opacity: 0.2;
  pointer-events: none;
  z-index: 0;
}

.orb-1 {
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(240, 208, 128, 0.5), transparent);
  top: -200px;
  right: -200px;
}

.orb-2 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(74, 77, 84, 0.35), transparent);
  bottom: -150px;
  left: -150px;
}

.settings-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
  position: relative;
  z-index: 2;
}

/* ==================== HEADER ==================== */
.settings-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 32px;
  padding: 32px;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 18px;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 8px;
  background: linear-gradient(135deg, var(--gold-light), var(--gold));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.ceo-page-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 12px;
  border-radius: 999px;
  background: linear-gradient(150deg, var(--gold-light), var(--gold));
  -webkit-background-clip: initial;
  -webkit-text-fill-color: #08090A;
  color: #08090A;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  box-shadow: 0 3px 12px rgba(201, 168, 76, 0.35);
}

.header-subtitle {
  font-size: 16px;
  color: var(--text-secondary);
}

.btn-back {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: var(--bg-alt);
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.btn-back:hover {
  background: var(--bg-card-hover);
  border-color: var(--border-gold);
}

.ceo-inline-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 9px;
  border-radius: 999px;
  background: linear-gradient(150deg, var(--gold-light), var(--gold));
  color: #08090A;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  margin-left: 8px;
  vertical-align: middle;
}

/* ==================== GRID ==================== */
.settings-grid {
  display: grid;
  grid-template-columns: 340px 1fr;
  gap: 24px;
}

/* ==================== SIDEBAR ==================== */
.sidebar-card {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.avatar-section {
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 18px;
  padding: 28px 24px;
  text-align: center;
}

.avatar-wrapper {
  position: relative;
  width: 88px;
  height: 88px;
  margin: 0 auto 16px;
}

.avatar {
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  border: 2px solid var(--border-gold);
}

.avatar-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #08090A;
  font-size: 28px;
  font-weight: 800;
}

.avatar-edit {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: linear-gradient(150deg, var(--gold-light), var(--gold));
  border: 2px solid var(--bg-card);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #08090A;
  cursor: pointer;
}

.user-name {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.user-email {
  font-size: 13px;
  color: var(--text-muted);
}

/* ==================== POINTS CARD ==================== */
.points-card {
  display: flex;
  gap: 16px;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 18px;
  padding: 22px;
}

.points-icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(201, 168, 76, 0.15);
  color: var(--gold-light);
  flex-shrink: 0;
}

.points-content { flex: 1; min-width: 0; }

.points-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
  margin-bottom: 4px;
}

.points-value {
  font-size: 30px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
}

.points-today {
  font-size: 12px;
  color: var(--gold-light);
  font-weight: 600;
  margin: 4px 0 12px;
}

.progress-bar {
  height: 6px;
  background: var(--bg-alt);
  border: 1px solid var(--border-subtle);
  border-radius: 999px;
  overflow: hidden;
  margin-bottom: 6px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--gold-dark), var(--gold-light));
  border-radius: 999px;
  transition: width 0.4s ease;
}

.progress-label { font-size: 11px; color: var(--text-muted); }

/* ==================== ACTIVITY CARD ==================== */
.activity-card {
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 18px;
  padding: 22px;
}

.activity-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  color: var(--gold-light);
  font-size: 14px;
  font-weight: 700;
}

.activity-empty {
  font-size: 12.5px;
  color: var(--text-muted);
  padding: 10px 0;
  text-align: center;
}

.activity-chart { margin-bottom: 14px; }

.activity-stats {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
}

.stat-item { display: flex; flex-direction: column; align-items: center; }
.stat-value { font-size: 16px; font-weight: 700; color: var(--text-primary); }
.stat-label { font-size: 10.5px; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; }
.stat-divider { color: var(--border-subtle); }

/* ==================== CONTENT CARD ==================== */
.content-card {
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 18px;
  overflow: hidden;
}

.tabs-header {
  display: flex;
  border-bottom: 1px solid var(--border-subtle);
}

.tab-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 18px 24px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-button.active {
  color: var(--gold-light);
  border-bottom-color: var(--gold);
}

.tab-content { padding: 28px; }

/* ==================== FORMS ==================== */
.settings-form { max-width: 480px; }

.form-group { margin-bottom: 18px; }

.form-group label {
  display: block;
  font-size: 12.5px;
  font-weight: 700;
  color: var(--text-secondary);
  margin-bottom: 8px;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 12px 16px;
  background: var(--bg-alt);
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  color: var(--text-primary);
  font-family: 'Syne', sans-serif;
  font-size: 14.5px;
  outline: none;
  transition: border-color 0.2s ease;
}

.form-group input:focus,
.form-group textarea:focus { border-color: var(--gold); }

.form-group input:disabled { opacity: 0.6; cursor: not-allowed; }

.form-hint { font-size: 11.5px; color: var(--text-muted); margin-top: 6px; }

.password-input { position: relative; display: flex; align-items: center; }

.btn-toggle-password {
  position: absolute;
  right: 12px;
  background: transparent;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
}

.password-strength { display: flex; align-items: center; gap: 10px; margin-top: 8px; }

.strength-bar {
  flex: 1;
  height: 5px;
  background: var(--border-subtle);
  border-radius: 999px;
  overflow: hidden;
}

.strength-fill { height: 100%; border-radius: 999px; transition: width 0.3s, background 0.3s; }
.strength-label { font-size: 11.5px; font-weight: 700; min-width: 55px; }

.form-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba(220, 53, 69, 0.12);
  border: 1px solid rgba(220, 53, 69, 0.3);
  border-radius: 8px;
  color: #E8969F;
  font-size: 13px;
  margin-bottom: 16px;
}

.btn-save {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  background: linear-gradient(150deg, var(--gold-light), var(--gold));
  border: none;
  border-radius: 10px;
  color: #08090A;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-save:hover:not(:disabled) { transform: translateY(-1px); }
.btn-save:disabled { opacity: 0.6; cursor: not-allowed; }

/* ==================== SECURITY NOTICE ==================== */
.security-notice {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: rgba(201, 168, 76, 0.08);
  border: 1px solid var(--border-gold);
  border-radius: 12px;
  margin-bottom: 24px;
}

.security-notice svg { color: var(--gold-light); flex-shrink: 0; }
.notice-title { font-size: 14px; font-weight: 700; color: var(--text-primary); }
.notice-text { font-size: 12.5px; color: var(--text-secondary); margin-top: 2px; }

/* ==================== 2FA ==================== */
.security-section {
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid var(--border-subtle);
  max-width: 480px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--gold-light);
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 16px;
}

.twofa-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  background: var(--bg-alt);
  border: 1px solid var(--border-subtle);
  border-radius: 12px;
}

.twofa-title { font-size: 13.5px; font-weight: 700; color: var(--text-primary); }
.twofa-subtitle { font-size: 12px; color: var(--text-muted); margin-top: 2px; }

.toggle-switch {
  position: relative;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  inset: 0;
  background: var(--border-subtle);
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  top: 3px;
  background: var(--text-primary);
  border-radius: 50%;
  transition: all 0.2s ease;
}

.toggle-switch input:checked + .toggle-slider {
  background: var(--gold);
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(20px);
  background: #08090A;
}

/* ==================== TOAST ==================== */
.toast-notification {
  position: fixed;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 20px;
  background: #0f1012;
  border: 1px solid var(--border-gold);
  border-radius: 12px;
  color: var(--text-primary);
  font-size: 14px;
  z-index: 600;
}

.toast-notification button {
  background: transparent;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
}

/* ==================== RESPONSIVE ==================== */
@media (max-width: 960px) {
  .settings-grid { grid-template-columns: 1fr; }
  .settings-header { flex-direction: column; align-items: flex-start; }
}
</style>